/**
 * /accounting/gl/chart-of-accounts — read-only Chart of Accounts.
 */
import { useMemo, useState } from "react";
import { CockpitPage, CockpitSection, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { Badge } from "@/components/ui/badge";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import type { GLAccountType } from "@/data/accounting/glTypes";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const TYPE_ORDER: GLAccountType[] = ["asset", "liability", "equity", "revenue", "expense"];
const TYPE_LABEL: Record<GLAccountType, string> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  revenue: "Revenue",
  expense: "Expenses",
};

export default function ChartOfAccountsPage() {
  const { accounts } = useGeneralLedger();
  const [q, setQ] = useState("");

  const grouped = useMemo(() => {
    const filt = accounts.filter(a => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (
        a.number.toLowerCase().includes(s) ||
        a.name.toLowerCase().includes(s) ||
        a.subtype.toLowerCase().includes(s)
      );
    });
    const by: Record<GLAccountType, typeof accounts> = {
      asset: [], liability: [], equity: [], revenue: [], expense: [],
    };
    filt.forEach(a => by[a.type].push(a));
    TYPE_ORDER.forEach(t => by[t].sort((x, y) => x.number.localeCompare(y.number)));
    return by;
  }, [accounts, q]);

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Chart of Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {accounts.length} accounts. Subledger-tagged accounts require customer / vendor / job on each line.
          </p>
        </div>
        <div className="w-72">
          <CockpitSearch value={q} onChange={setQ} placeholder="Search by number, name, type…" />
        </div>
      </div>

      {TYPE_ORDER.map(t => {
        const rows = grouped[t];
        if (!rows.length) return null;
        return (
          <CockpitSection key={t}>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              {TYPE_LABEL[t]} <span className="ml-1 text-muted-foreground/70">({rows.length})</span>
            </div>
            <div className="rounded-md border border-border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 w-24">Number</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2 w-40">Subtype</th>
                    <th className="px-3 py-2 w-28">Normal</th>
                    <th className="px-3 py-2 w-40">Subledger</th>
                    <th className="px-3 py-2 w-28">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(a => (
                    <tr key={a.id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{a.number}</td>
                      <td className="px-3 py-2">
                        <div>{a.name}</div>
                        {a.description ? (
                          <div className="text-xs text-muted-foreground mt-0.5">{a.description}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {a.subtype.replace(/_/g, " ")}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {a.normalBalance === "debit" ? "DR" : "CR"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {a.requiresSubledger ? (
                          <Badge variant="outline" className="text-[10px]">
                            {a.requiresSubledger}
                          </Badge>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {a.active ? "Active" : <span className="text-muted-foreground">Inactive</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CockpitSection>
        );
      })}
    </CockpitPage>
  );
}
